<template>
  <!-- The support chat button, bottom right on every signed-in page. It
       shows how many conversations have replies waiting (and peeks the
       newest), and opens into the chat, all under "Chat with us!": Home (a
       hello, the latest conversation, and "Send us a message"), Messages
       (every conversation the organization has had, each its own question,
       newest first) and the conversation itself. When staff
       close a conversation it asks for a rating, and a new message starts a
       new conversation. "—" tucks the chat away where it was; "×" closes it,
       back to Home. It fills the screen on a phone.

       ?support-chat=open opens it, ?support-chat=<id> opens a conversation
       (the emails' button) and ?support-chat=new starts one. The same file
       lives in Screenkode and Chabad Digital: App.vue passes the team's name
       and logo. -->
  <div class="scw" :class="{ 'scw--open': open, 'scw--lifted': liftForBottomNav }">
    <transition name="scw-panel">
      <section
        v-if="hasOpened"
        v-show="open"
        ref="panel"
        class="scw-panel"
        :class="`scw-panel--${view}`"
        role="dialog"
        tabindex="-1"
        :aria-label="panelLabel"
        @keydown.esc="onEscape"
      >
        <!-- "Chat with us!" (or "Messages"), white, over the conversations
             and a conversation: the title, the minimize and close buttons —
             and, in a conversation, the way back. Home has its own, on a soft
             tint of the team's colour. -->
        <header v-if="view !== 'home'" class="scw-head">
          <button
            v-if="view === 'conversation'"
            type="button"
            class="scw-icon-btn scw-head__back"
            title="Back"
            :aria-label="backView === 'home' ? 'Back to home' : 'Back to your conversations'"
            @click="goBack"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 6l-6 6 6 6" /></svg>
          </button>
          <h2 class="scw-head__title">{{ headTitle }}</h2>
          <button type="button" class="scw-icon-btn scw-head__minimize" title="Minimize" aria-label="Minimize the chat" @click="minimize">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5.5 12h13" /></svg>
          </button>
          <button type="button" class="scw-icon-btn scw-head__close" title="Close" aria-label="Close the chat" @click="closeChat">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.75 6.75l10.5 10.5M17.25 6.75L6.75 17.25" /></svg>
          </button>
        </header>

        <!-- Home: the hello on a soft tint of the team's colour, then the
             latest conversation, "Send us a message", every conversation, and
             how to reach a person by phone. -->
        <div v-if="view === 'home'" key="home" class="scw-view scw-home">
          <div class="scw-hero">
            <div class="scw-hero__bar">
              <span class="scw-hero__logo" aria-hidden="true"><img :src="teamLogo" alt="" /></span>
              <span class="scw-hero__team">{{ teamName }}</span>
              <button
                type="button"
                class="scw-icon-btn scw-icon-btn--hero scw-head__minimize"
                title="Minimize"
                aria-label="Minimize the chat"
                @click="minimize"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5.5 12h13" /></svg>
              </button>
              <button type="button" class="scw-icon-btn scw-icon-btn--hero scw-head__close" title="Close" aria-label="Close the chat" @click="closeChat">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.75 6.75l10.5 10.5M17.25 6.75L6.75 17.25" /></svg>
              </button>
            </div>
            <h2 class="scw-home__title">{{ greeting }} <span class="scw-home__wave" aria-hidden="true">👋</span></h2>
            <p class="scw-home__text">How can we help?</p>
          </div>

          <div class="scw-home__cards">
            <div v-if="!rowsLoaded" class="scw-card scw-card--loading" aria-hidden="true">
              <span class="scw-skeleton scw-skeleton--label"></span>
              <span class="scw-skeleton scw-skeleton--line"></span>
              <span class="scw-skeleton scw-skeleton--short"></span>
            </div>
            <button
              v-else-if="recentRow"
              type="button"
              class="scw-card scw-recent"
              :class="{ 'is-unread': unreadOf(recentRow) > 0 }"
              @click="openConversation(recentRow.conversation)"
            >
              <span class="scw-card__label">{{ unreadOf(recentRow) ? 'New reply' : 'Recent chat' }}</span>
              <span class="scw-recent__row">
                <span class="scw-avatar" aria-hidden="true"><img :src="teamLogo" alt="" /></span>
                <span class="scw-recent__body">
                  <span class="scw-recent__title">{{ recentRow.conversation.title }}</span>
                  <span class="scw-recent__preview">{{ previewOf(recentRow) }}</span>
                </span>
                <span class="scw-recent__meta">
                  <span class="scw-recent__time">{{ chatAgo(recentRow.conversation.lastMessageAt) }}</span>
                  <span v-if="unreadOf(recentRow)" class="scw-dot" aria-label="Unread"></span>
                  <i v-else class="pi pi-angle-right" aria-hidden="true"></i>
                </span>
              </span>
            </button>

            <button type="button" class="scw-card scw-send" @click="newConversation">
              <span class="scw-send__text">
                <strong>Send us a message</strong>
                <span>We'll reply right here, and by email if you're away</span>
              </span>
              <span class="scw-send__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M21.05 3.05c-2.18-2.35-18.56 3.4-18.55 5.5.02 2.38 6.4 3.11 8.17 3.61 1.06.3 1.35.6 1.6 1.72 1.1 5.05 1.66 7.57 2.93 7.62 2.03.09 7.97-16.16 5.85-18.45Z" />
                  <path d="M11.5 12.5L15 9" />
                </svg>
              </span>
            </button>

            <button v-if="rows.length > 1" type="button" class="scw-card scw-all" @click="showView('messages')">
              <span class="scw-all__label">Your conversations</span>
              <span class="scw-all__count">{{ rows.length }}{{ rowsHasMore ? '+' : '' }}</span>
              <i class="pi pi-angle-right" aria-hidden="true"></i>
            </button>

            <section class="scw-help" aria-label="Need help?">
              <h3 class="scw-help__title">Need Help?</h3>
              <p class="scw-help__text">This is a real person, not an AI. Our support team is here to help.</p>
              <p class="scw-help__text">
                For additional assistance or to speak with someone directly, call
                <a class="scw-help__phone" :href="supportPhone.href">{{ supportPhone.label }}</a>.
              </p>
              <div class="scw-help__hours">
                <h4 class="scw-help__label">Business Hours</h4>
                <dl class="scw-help__list">
                  <div v-for="hours in supportHours" :key="hours.days" class="scw-help__row">
                    <dt>{{ hours.days }}</dt>
                    <dd>{{ hours.time }}</dd>
                  </div>
                </dl>
              </div>
            </section>
          </div>
        </div>

        <!-- Messages -->
        <div v-else-if="view === 'messages'" key="messages" class="scw-view scw-messages">
          <div ref="list" class="scw-list" @scroll.passive="onListScroll">
            <div v-if="!rowsLoaded" class="scw-list__card" aria-hidden="true">
              <div v-for="index in 4" :key="index" class="scw-row scw-row--skeleton">
                <span class="scw-skeleton scw-skeleton--avatar"></span>
                <span class="scw-row__body">
                  <span class="scw-skeleton scw-skeleton--line"></span>
                  <span class="scw-skeleton scw-skeleton--short"></span>
                </span>
              </div>
            </div>
            <div v-else-if="!rows.length" class="scw-list__empty">
              <span class="scw-list__empty-icon" aria-hidden="true"><i class="pi pi-comments"></i></span>
              <h3>No messages yet</h3>
              <p>Questions, a screen acting up, an idea? Send us a message — each one becomes a chat of its own here.</p>
            </div>
            <div v-else class="scw-list__card">
              <button
                v-for="row in rows"
                :key="row.conversation.id"
                type="button"
                class="scw-row"
                :class="{ 'is-unread': unreadOf(row) > 0, 'is-closed': row.conversation.status === 'resolved' }"
                @click="openConversation(row.conversation)"
              >
                <span class="scw-avatar" aria-hidden="true"><img :src="teamLogo" alt="" /></span>
                <span class="scw-row__body">
                  <span class="scw-row__top">
                    <span class="scw-row__title">{{ row.conversation.title }}</span>
                    <span class="scw-row__time">{{ chatAgo(row.conversation.lastMessageAt) }}</span>
                  </span>
                  <span class="scw-row__bottom">
                    <span class="scw-row__preview">{{ previewOf(row) }}</span>
                    <span v-if="unreadOf(row)" class="scw-dot" :aria-label="`${unreadOf(row)} unread`"></span>
                    <span v-else-if="row.conversation.status === 'resolved'" class="scw-row__closed">Closed</span>
                  </span>
                </span>
              </button>
            </div>
            <div v-if="loadingMore" class="scw-list__more" role="status">
              <span class="sc-spinner" aria-hidden="true"></span>
            </div>
          </div>

          <button type="button" class="scw-fab" @click="newConversation">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21.05 3.05c-2.18-2.35-18.56 3.4-18.55 5.5.02 2.38 6.4 3.11 8.17 3.61 1.06.3 1.35.6 1.6 1.72 1.1 5.05 1.66 7.57 2.93 7.62 2.03.09 7.97-16.16 5.85-18.45Z" />
              <path d="M11.5 12.5L15 9" />
            </svg>
            <span>Send us a message</span>
          </button>
        </div>

        <!-- A conversation -->
        <div v-else key="conversation" class="scw-view scw-conversation">
          <chat-thread
            :key="paneKey"
            ref="thread"
            side="organization"
            layout="popup"
            :conversation-id="conversationId"
            :active="open"
            :peer-name="teamName"
            :peer-avatar="{ name: teamName, image: teamLogo, square: true }"
            compact
            @conversation="onConversation"
            @started="onStarted"
            @restarted="onRestarted"
            @sent="refreshRowsSoon"
          >
            <!-- A new chat opens with the team's hello, drawn as its messages are. -->
            <template #empty>
              <div class="sc-msg sc-msg--theirs sc-msg--first scw-intro">
                <div class="sc-msg__main">
                  <div class="sc-msg__head">
                    <chat-avatar :name="teamName" :image="teamLogo" square :size="22" />
                    <span class="sc-msg__head-name">{{ teamName }}</span>
                  </div>
                  <div class="sc-msg__stack">
                    <p class="sc-bubble scw-intro__bubble">{{ hello }}</p>
                    <p class="sc-bubble scw-intro__bubble">Welcome to {{ teamName }}.<br />Let us know how we can help.</p>
                  </div>
                </div>
              </div>
            </template>
            <!-- Where staff closed the chat: the stars and a few words, sent
                 together; once rated, just the thanks and the stars given. -->
            <template #closed="{ conversation }">
              <div class="scw-rate" :class="{ 'is-rated': isRated(conversation) }">
                <span class="scw-rate__badge" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M12 3.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L12 16.78l-5.2 2.74.99-5.8-4.21-4.1 5.82-.85z" /></svg>
                </span>
                <p class="scw-rate__text">
                  This chat is marked closed by {{ closerOf(conversation) }}.<br />
                  {{ isRated(conversation) ? 'Thank you for rating your experience.' : 'You can rate the experience now.' }}
                </p>
                <div
                  v-if="isRated(conversation)"
                  class="scw-rate__stars scw-rate__stars--given"
                  role="img"
                  :aria-label="`Rated ${conversation.rating} out of 5`"
                >
                  <svg
                    v-for="star in 5"
                    :key="star"
                    class="scw-rate__given"
                    :class="{ 'is-on': star <= conversation.rating }"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  ><path d="M12 2.6l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.41l-5.8 3.05 1.11-6.46-4.7-4.58 6.49-.94z" /></svg>
                </div>
                <template v-else>
                  <div class="scw-rate__stars" role="radiogroup" aria-label="Rate this chat" @mouseleave="hoverStars = 0">
                    <button
                      v-for="star in 5"
                      :key="star"
                      type="button"
                      role="radio"
                      class="scw-rate__star"
                      :class="{ 'is-on': star <= shownStars }"
                      :aria-checked="pickedStars === star ? 'true' : 'false'"
                      :aria-label="`${star} star${star === 1 ? '' : 's'}`"
                      :disabled="savingRating"
                      @mouseenter="hoverStars = star"
                      @focus="hoverStars = star"
                      @blur="hoverStars = 0"
                      @click="pickStars(star)"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.6l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.41l-5.8 3.05 1.11-6.46-4.7-4.58 6.49-.94z" /></svg>
                    </button>
                  </div>
                  <textarea
                    v-model="feedbackDraft"
                    class="scw-rate__comment"
                    rows="3"
                    :maxlength="commentMax"
                    :disabled="savingRating"
                    placeholder="Write something down..."
                    aria-label="Tell us about your experience (optional)"
                  ></textarea>
                  <p v-if="ratingError" class="scw-rate__error" role="alert">{{ ratingError }}</p>
                  <div v-if="pickedStars" class="scw-rate__foot">
                    <button type="button" class="scw-rate__send" :disabled="savingRating" @click="submitRating(conversation)">
                      {{ savingRating ? 'Submitting…' : 'Submit' }}
                    </button>
                  </div>
                </template>
              </div>
            </template>
          </chat-thread>
        </div>

        <nav v-if="view !== 'conversation'" class="scw-nav" aria-label="Support chat">
          <button
            type="button"
            class="scw-nav__item"
            :class="{ 'is-active': view === 'home' }"
            :aria-current="view === 'home' ? 'page' : null"
            @click="showView('home')"
          >
            <span class="scw-nav__icon"><i class="pi pi-home" aria-hidden="true"></i></span>
            <span>Home</span>
          </button>
          <button
            type="button"
            class="scw-nav__item"
            :class="{ 'is-active': view === 'messages' }"
            :aria-current="view === 'messages' ? 'page' : null"
            :aria-label="unreadConversations ? `Messages, ${unreadConversations} unread` : 'Messages'"
            @click="showView('messages')"
          >
            <span class="scw-nav__icon">
              <i class="pi pi-comments" aria-hidden="true"></i>
              <span v-if="unreadConversations" class="scw-nav__badge" aria-hidden="true">{{ unreadConversations > 9 ? '9+' : unreadConversations }}</span>
            </span>
            <span>Messages</span>
          </button>
        </nav>
      </section>
    </transition>

    <transition name="scw-peek">
      <div v-if="peek && !open" class="scw-peek" role="status">
        <button type="button" class="scw-peek__main" :aria-label="`Open the chat: new message from ${peekAuthor}`" @click="openPeek">
          <span class="scw-avatar" aria-hidden="true"><img :src="teamLogo" alt="" /></span>
          <span class="scw-peek__body">
            <span class="scw-peek__who">{{ peekAuthor }}</span>
            <span v-if="peek.title" class="scw-peek__title">{{ peek.title }}</span>
            <span class="scw-peek__text">
              <i v-if="peekIcon" class="pi" :class="peekIcon" aria-hidden="true"></i>
              {{ peek.preview }}
            </span>
          </span>
        </button>
        <button type="button" class="scw-peek__close" aria-label="Dismiss" title="Dismiss" @click="dismissPeek">
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </div>
    </transition>

    <button
      ref="launcher"
      type="button"
      class="scw-launcher"
      :class="{ 'is-open': open, 'has-unread': unreadConversations > 0 }"
      :aria-label="launcherLabel"
      :aria-expanded="open ? 'true' : 'false'"
      :title="open ? 'Minimize the chat' : 'Chat with us'"
      @click="toggle"
    >
      <!-- The chat bubble with its three dots: the design's icon (Figma, 24px). -->
      <span class="scw-launcher__icon scw-launcher__icon--chat" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M12.75 18.375H18a4 4 0 0 0 4-4V6.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v7.875a4 4 0 0 0 4 4h1.5l1.25 3.375Z" />
          <path class="scw-launcher__dots" d="M7.99 10.5h.02M11.99 10.5h.02M15.99 10.5h.02" />
        </svg>
      </span>
      <span class="scw-launcher__icon scw-launcher__icon--close" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M6.5 9.5l5.5 5.5 5.5-5.5" /></svg>
      </span>
      <span v-if="unreadConversations && !open" class="scw-launcher__badge" aria-hidden="true">{{ unreadConversations > 9 ? '9+' : unreadConversations }}</span>
    </button>
  </div>
</template>
<script>
import chatAvatar from '@/components/chat/Chat_Avatar.vue';
import chatThread from '@/components/chat/Chat_Thread.vue';
import {
  chatAgo,
  chatFirstName,
  chatPreviewText,
  CHAT_LIST_PAGE_SIZE,
  CHAT_LIST_POLL_MS,
  CHAT_RATING_COMMENT_MAX_LENGTH,
  fetchChatConversations,
  rateChatConversation,
  setChatAppSide,
} from '@/utils/chat';
import { onChatEvent } from '@/utils/chat_realtime';
import { chatUnread, startChatNotifications, stopChatNotifications } from '@/utils/chat_notifications';

const OPEN_KEY = 'screenkode.supportChat.open';
const VIEW_KEY = 'screenkode.supportChat.view';
const CONVERSATION_KEY = 'screenkode.supportChat.conversation';
const DISMISSED_KEY = 'screenkode.supportChat.dismissed';
const OPEN_QUERY = 'support-chat';

const VIEWS = ['home', 'messages', 'conversation'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// A reload of the list asks for as many as are shown — up to this many.
const LIST_KEEP_MAX = 200;

const PEEK_ICONS = { voice: 'pi-microphone', image: 'pi-image', file: 'pi-paperclip' };

// Home's "Need Help?": the line where a person answers, and when.
const SUPPORT_PHONE = { label: '718-663-4245, Ext. 5', href: 'tel:+17186634245,5' };
const SUPPORT_HOURS = [
  { days: 'Monday–Thursday', time: '9:00 AM–7:00 PM' },
  { days: 'Friday', time: '9:00 AM–1:00 PM' },
];

// Storage that may be blocked (a private window, a support session's frame).
const read = (storage, key) => {
  try {
    return window[storage].getItem(key);
  } catch (error) {
    return null;
  }
};
const write = (storage, key, value) => {
  try {
    if (value === null) window[storage].removeItem(key);
    else window[storage].setItem(key, value);
  } catch (error) {
    // Not kept; the widget works the same.
  }
};

const isTouch = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

export default {
  name: 'SupportChatWidget',
  components: {
    chatAvatar,
    chatThread,
  },
  props: {
    // Who the organization is talking to: "Screenkode", "Chabad Digital".
    teamName: {
      type: String,
      required: true,
    },
    teamLogo: {
      type: String,
      required: true,
    },
    // Sit above the phone's bottom navigation.
    liftForBottomNav: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const open = read('sessionStorage', OPEN_KEY) === '1';
    const storedView = read('sessionStorage', VIEW_KEY);
    const storedConversation = read('sessionStorage', CONVERSATION_KEY);
    const conversationId = UUID.test(String(storedConversation || '')) ? storedConversation : null;
    let view = VIEWS.includes(storedView) ? storedView : 'home';
    // A conversation view needs a conversation, or a new one being written.
    if (view === 'conversation' && !conversationId && storedConversation !== 'new') view = 'home';
    return {
      open,
      // The panel is only made once the chat is first opened.
      hasOpened: open,
      view,
      // Where the back button in a conversation leads.
      backView: 'messages',
      // The conversation shown; null for a new one, which its first message starts.
      conversationId: view === 'conversation' ? conversationId : null,
      // The conversation as it was last seen: its title, status and rating.
      current: null,
      // What the conversation is keyed by: a new one keeps it once its first message starts it.
      paneKey: view === 'conversation' ? conversationId || 'new' : '',
      // The organization's conversations: { organization, conversation }, newest first.
      rows: [],
      rowsLoaded: false,
      rowsHasMore: false,
      loadingMore: false,
      peek: null,
      dismissedPeek: read('sessionStorage', DISMISSED_KEY),
      // The rating card of a closed conversation: the stars picked and the
      // words written, sent together.
      hoverStars: 0,
      pickedStars: 0,
      feedbackDraft: '',
      savingRating: false,
      ratingError: '',
      commentMax: CHAT_RATING_COMMENT_MAX_LENGTH,
      supportPhone: SUPPORT_PHONE,
      supportHours: SUPPORT_HOURS,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account?.data || null;
    },
    myId() {
      return this.account?.id || null;
    },
    firstName() {
      const name = String(this.account?.fname || '').trim();
      return name ? `${name.charAt(0).toUpperCase()}${name.slice(1)}` : '';
    },
    greeting() {
      return this.firstName ? `Hi ${this.firstName}!` : 'Hi there!';
    },
    hello() {
      return this.firstName ? `Hello ${this.firstName}! 👋` : 'Hello there! 👋';
    },
    unreadConversations() {
      return chatUnread.conversations;
    },
    latest() {
      return chatUnread.latest;
    },
    // Home's card: the newest conversation with a reply waiting, else the newest still open.
    recentRow() {
      return this.rows.find((row) => this.unreadOf(row) > 0)
        || this.rows.find((row) => row.conversation.status === 'open')
        || null;
    },
    headTitle() {
      return this.view === 'messages' ? 'Messages' : 'Chat with us!';
    },
    // The header says "Chat with us!"; the conversation's own title is in the
    // panel's name, for a screen reader.
    panelLabel() {
      const title = this.view === 'conversation' && this.conversationId && this.current ? this.current.title : '';
      return title ? `${this.teamName} support chat — ${title}` : `${this.teamName} support chat`;
    },
    currentId() {
      return this.current ? this.current.id : null;
    },
    // The stars lit on the rating card: the ones pointed at, else the ones
    // picked — and, before any are, all five, to be picked from.
    shownStars() {
      return this.hoverStars || this.pickedStars || 5;
    },
    peekAuthor() {
      const name = this.peek?.authorName;
      return name ? `${name} from ${this.teamName}` : `${this.teamName} Support`;
    },
    peekIcon() {
      return this.peek ? PEEK_ICONS[this.peek.kind] || '' : '';
    },
    launcherLabel() {
      if (this.open) return 'Minimize the support chat';
      if (!this.unreadConversations) return 'Open the support chat';
      return `Open the support chat — ${this.unreadConversations} ${this.unreadConversations === 1 ? 'conversation has' : 'conversations have'} new replies`;
    },
  },
  watch: {
    open(isOpen) {
      write('sessionStorage', OPEN_KEY, isOpen ? '1' : null);
      if (isOpen) {
        this.loadRows(this.rowsLoaded);
        this.schedule();
      } else {
        window.clearTimeout(this.pollTimer);
      }
    },
    view(value) {
      write('sessionStorage', VIEW_KEY, value);
      if (value !== 'conversation') this.loadRows(true);
    },
    conversationId(id) {
      write('sessionStorage', CONVERSATION_KEY, this.view === 'conversation' ? id || 'new' : null);
    },
    // Another conversation: its rating card starts afresh.
    currentId() {
      this.hoverStars = 0;
      this.pickedStars = 0;
      this.feedbackDraft = '';
      this.ratingError = '';
    },
    // A new reply while the chat is closed peeks out above the button.
    latest: {
      immediate: true,
      handler(latest) {
        if (!latest) {
          this.peek = null;
          return;
        }
        if (this.open || latest.id === this.dismissedPeek) return;
        this.peek = latest;
      },
    },
    // ?support-chat=open (or a conversation's id, or new) — the emails' button.
    '$route.query': {
      immediate: true,
      handler(query) {
        const asked = query ? query[OPEN_QUERY] : null;
        if (!asked || typeof asked !== 'string') return;
        if (UUID.test(asked)) this.openChat({ conversationId: asked });
        else if (asked === 'new') this.openChat({ startNew: true });
        else this.openChat();
        const rest = { ...query };
        delete rest[OPEN_QUERY];
        this.$router.replace({ query: rest }).catch(() => {});
      },
    },
  },
  methods: {
    chatAgo,
    unreadOf(row) {
      return Number(row && row.conversation && row.conversation.unreadCount) || 0;
    },
    // "You: …", "Abel: …", "Sara: …" — or what a conversation that only just started says.
    previewOf(row) {
      const last = row.conversation.lastMessage;
      if (!last) return `${this.teamName} Support`;
      return chatPreviewText(last, { viewerId: this.myId, viewerSide: 'organization' });
    },

    /* ------------------------------ opening ------------------------------ */
    toggle() {
      if (this.open) this.minimize();
      else this.openChat();
    },
    // Opens the chat where it was, or on a conversation, or a new one.
    openChat({ conversationId = null, startNew = false } = {}) {
      this.hasOpened = true;
      this.open = true;
      this.dismissPeek();
      if (conversationId) this.openConversation({ id: conversationId });
      else if (startNew) this.newConversation();
      this.$nextTick(() => this.focusView());
    },
    // "—": tucked away, to open again where it was.
    minimize() {
      this.open = false;
      this.$nextTick(() => this.$refs.launcher?.focus());
    },
    // "×": closed, to open again on Home.
    closeChat() {
      this.open = false;
      this.view = 'home';
      this.conversationId = null;
      this.current = null;
      this.paneKey = '';
      write('sessionStorage', CONVERSATION_KEY, null);
      this.$nextTick(() => this.$refs.launcher?.focus());
    },
    onEscape(event) {
      // Escape inside a picker, a menu or an edit belongs to them.
      if (event.defaultPrevented) return;
      this.minimize();
    },
    // The cursor goes where it is useful: the composer of a conversation (not
    // on a phone, where the keyboard would cover it), else the panel itself.
    focusView() {
      if (!this.open) return;
      if (this.view === 'conversation' && !isTouch()) {
        const input = this.$refs.panel?.querySelector('.sc-composer__input');
        if (input) {
          input.focus();
          return;
        }
      }
      this.$refs.panel?.focus({ preventScroll: true });
    },
    showView(view) {
      this.view = view;
      this.$nextTick(() => this.focusView());
    },
    openConversation(conversation) {
      if (!conversation || !conversation.id) return;
      if (this.view !== 'conversation') this.backView = this.view === 'home' ? 'home' : 'messages';
      const row = this.rows.find((item) => item.conversation.id === conversation.id);
      this.current = conversation.title ? conversation : (row ? row.conversation : null);
      this.conversationId = conversation.id;
      this.paneKey = conversation.id;
      this.view = 'conversation';
      write('sessionStorage', CONVERSATION_KEY, conversation.id);
      this.$nextTick(() => this.focusView());
    },
    // A new conversation: written here, started by its first message.
    newConversation() {
      if (this.view !== 'conversation') this.backView = this.view === 'home' ? 'home' : 'messages';
      this.current = null;
      this.conversationId = null;
      this.paneKey = `new:${Date.now()}`;
      this.view = 'conversation';
      write('sessionStorage', CONVERSATION_KEY, 'new');
      this.$nextTick(() => this.focusView());
    },
    goBack() {
      this.showView(this.backView || 'messages');
    },
    openPeek() {
      const conversationId = this.peek && this.peek.conversationId;
      if (conversationId) this.openChat({ conversationId });
      else this.openChat();
    },
    dismissPeek() {
      const id = this.peek?.id || this.latest?.id || null;
      this.peek = null;
      if (id) {
        this.dismissedPeek = id;
        write('sessionStorage', DISMISSED_KEY, id);
      }
    },

    /* --------------------------- the conversation ------------------------ */
    // The conversation as its thread last loaded it: the header and its row follow.
    onConversation(conversation) {
      if (!conversation || !conversation.id) return;
      if (conversation.id === this.conversationId) this.current = conversation;
      this.rows = this.rows.map((row) => (row.conversation.id === conversation.id
        ? { ...row, conversation: { ...row.conversation, ...conversation, lastMessage: conversation.lastMessage || row.conversation.lastMessage } }
        : row));
    },
    // A new conversation's first message started it: the same pane carries on as it.
    onStarted(conversation) {
      this.current = conversation;
      this.conversationId = conversation.id;
      write('sessionStorage', CONVERSATION_KEY, conversation.id);
      this.refreshRowsSoon();
    },
    // Written in a closed conversation: a new one takes its place here.
    onRestarted() {
      this.current = null;
      this.conversationId = null;
      write('sessionStorage', CONVERSATION_KEY, 'new');
    },

    /* ----------------------------- the rating ---------------------------- */
    // "This chat is marked closed by Aron."
    closerOf(conversation) {
      return chatFirstName(conversation && conversation.resolvedBy, 'our team');
    },
    // Rated: the card only thanks them and shows the stars they gave.
    isRated(conversation) {
      return Number(conversation && conversation.rating) >= 1;
    },
    pickStars(stars) {
      this.pickedStars = stars;
      this.ratingError = '';
    },
    // What the API answers is the conversation as it now stands: the thread,
    // the header and the list all take it.
    tookRating(conversation) {
      if (this.$refs.thread) this.$refs.thread.setConversation(conversation);
      else this.onConversation(conversation);
    },
    // The stars picked and the words written (if any), in one go.
    async submitRating(conversation) {
      if (!conversation || !this.pickedStars || this.savingRating) return;
      this.savingRating = true;
      this.ratingError = '';
      const comment = this.feedbackDraft.trim();
      const result = await rateChatConversation(conversation.id, comment ? { rating: this.pickedStars, comment } : { rating: this.pickedStars });
      if (this.isGone) return;
      this.savingRating = false;
      if (result.ok && result.data) {
        this.tookRating(result.data);
        return;
      }
      this.ratingError = "Your rating couldn't be sent. Please try again.";
    },

    /* ------------------------------ the list ----------------------------- */
    async loadRows(keep = false) {
      if (!this.hasOpened || this.loadingRows) return;
      this.loadingRows = true;
      try {
        const limit = keep ? Math.min(Math.max(this.rows.length, CHAT_LIST_PAGE_SIZE), LIST_KEEP_MAX) : CHAT_LIST_PAGE_SIZE;
        const result = await fetchChatConversations({ limit });
        if (this.isGone) return;
        if (result.ok && result.data) {
          this.rows = (Array.isArray(result.data.conversations) ? result.data.conversations : []).filter((row) => row && row.conversation);
          this.rowsHasMore = Boolean(result.data.hasMore);
        }
        this.rowsLoaded = true;
      } finally {
        this.loadingRows = false;
      }
    },
    async loadMore() {
      const last = this.rows[this.rows.length - 1];
      if (this.loadingMore || !this.rowsHasMore || !last) return;
      this.loadingMore = true;
      const result = await fetchChatConversations({ limit: CHAT_LIST_PAGE_SIZE, before: last.conversation.lastMessageAt });
      if (this.isGone) return;
      this.loadingMore = false;
      if (!result.ok || !result.data) return;
      const known = new Set(this.rows.map((row) => row.conversation.id));
      this.rows = [...this.rows, ...(result.data.conversations || []).filter((row) => row && row.conversation && !known.has(row.conversation.id))];
      this.rowsHasMore = Boolean(result.data.hasMore);
    },
    onListScroll() {
      const list = this.$refs.list;
      if (list && list.scrollHeight - list.scrollTop - list.clientHeight < 120) this.loadMore();
    },
    refreshRowsSoon() {
      window.clearTimeout(this.soonTimer);
      this.soonTimer = window.setTimeout(() => this.loadRows(true), 400);
    },
    // While the chat is open the list keeps itself current — the live
    // notices ask at once, this is the safety net under them.
    schedule() {
      window.clearTimeout(this.pollTimer);
      if (this.isGone || !this.open) return;
      this.pollTimer = window.setTimeout(async () => {
        if (document.visibilityState !== 'hidden' && this.view !== 'conversation') await this.loadRows(true);
        this.schedule();
      }, CHAT_LIST_POLL_MS);
    },
    onLiveEvent(event) {
      if (event.type === 'typing' || !this.hasOpened) return;
      this.refreshRowsSoon();
    },
  },
  created() {
    // Everyone in an organization's app chats as the organization — a
    // Screenkode staff account that belongs to it too.
    setChatAppSide('organization');
    this.isGone = false;
    this.loadingRows = false;
    this.pollTimer = null;
    this.soonTimer = null;
  },
  mounted() {
    const store = this.$store;
    startChatNotifications(
      () => Boolean(store.getters.sessionToken && store.getters.account?.data),
      () => store.getters.sessionToken || null
    );
    this.stopListening = onChatEvent(this.onLiveEvent);
    if (this.open) {
      this.loadRows();
      this.schedule();
    }
  },
  beforeUnmount() {
    this.isGone = true;
    window.clearTimeout(this.pollTimer);
    window.clearTimeout(this.soonTimer);
    if (this.stopListening) this.stopListening();
    stopChatNotifications();
  },
};
</script>
<style scoped>
/* =========================================================================
   The floating support chat: a round button bottom right, the chat above
   it. Flat — Inter, a pale page, white cards with a hairline border, one
   solid indigo and no gradients — in its own palette, the same in both
   apps. Sizes are the design's at the apps' 90% zoom (html.app-zoomed).
   Above the page and the phone's bottom navigation (900), under dialogs
   (1000 and up) so it never covers a dialog's buttons.
   ========================================================================= */
.scw {
  --scw-gap: 24px;
  --scw-lift: 0px;
  --scw-accent: #4B3FE5;
  --scw-accent-hover: #3E32D2;
  --scw-accent-soft: #EEEDFD;
  /* Home's send button: a lighter shade of the accent, the accent itself on hover. */
  --scw-send: #786FEB;
  --scw-text: #18181B;
  --scw-text-2: #52525B;
  --scw-text-3: #71717A;
  --scw-text-4: #A1A1AA;
  --scw-border: #E4E4E7;
  --scw-divider: #F0F0F1;
  --scw-page: #FAFAFA;
  --scw-hero: #EEF0FF;
  --scw-surface: #FFFFFF;
  --scw-unread: #EF4444;
  --scw-star: #F59E0B;
  --scw-star-off: #E4E4E7;
  position: fixed;
  right: var(--scw-gap);
  bottom: calc(var(--scw-gap) + var(--scw-lift));
  z-index: 980;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 14px;
  color: var(--scw-text);
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  pointer-events: none;
  -webkit-font-smoothing: antialiased;
}

.scw > * {
  pointer-events: auto;
}

.scw button,
.scw textarea {
  font-family: inherit;
}

/* ------------------------------- the button ------------------------------ */
.scw-launcher {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--scw-accent);
  color: #FFFFFF;
  box-shadow: 0 6px 16px rgba(24, 24, 27, 0.18), 0 1px 4px rgba(24, 24, 27, 0.1);
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease, background-color 0.15s ease;
}

.scw-launcher:hover {
  background: var(--scw-accent-hover);
  box-shadow: 0 10px 22px rgba(24, 24, 27, 0.22), 0 2px 6px rgba(24, 24, 27, 0.12);
  transform: translateY(-2px);
}

.scw-launcher:active {
  transform: scale(0.95);
}

.scw-launcher:focus-visible {
  outline: 3px solid rgba(75, 63, 229, 0.35);
  outline-offset: 3px;
}

.scw-launcher__icon {
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.scw-launcher__icon svg {
  width: 26px;
  height: 26px;
  fill: none;
  stroke: #FFFFFF;
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.scw-launcher__icon svg .scw-launcher__dots {
  stroke-width: 2;
}

.scw-launcher__icon--close {
  opacity: 0;
  transform: rotate(-90deg) scale(0.6);
}

.scw-launcher__icon--close svg {
  width: 24px;
  height: 24px;
  stroke-width: 1.75;
}

.scw-launcher.is-open .scw-launcher__icon--chat {
  opacity: 0;
  transform: rotate(90deg) scale(0.6);
}

.scw-launcher.is-open .scw-launcher__icon--close {
  opacity: 1;
  transform: none;
}

.scw-launcher__badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 21px;
  height: 21px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--scw-unread);
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  line-height: 21px;
  text-align: center;
  box-shadow: 0 0 0 2px #FFFFFF;
  animation: scw-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes scw-pop {
  from { transform: scale(0.4); }
  to { transform: scale(1); }
}

/* -------------------------------- avatars -------------------------------- */
/* The team's mark, in a pale accent circle. */
.scw-avatar {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  overflow: hidden;
  border-radius: 50%;
  background: var(--scw-accent-soft);
}

.scw-avatar img {
  width: 62%;
  height: 62%;
  object-fit: contain;
}

/* ------------------------------- the peek -------------------------------- */
.scw-peek {
  position: relative;
  display: flex;
  align-items: stretch;
  width: 320px;
  max-width: calc(100vw - 2 * var(--scw-gap));
  border: 1px solid var(--scw-border);
  border-radius: 12px;
  background: var(--scw-surface);
  box-shadow: 0 14px 36px rgba(24, 24, 27, 0.14);
}

.scw-peek__main {
  display: flex;
  flex: 1;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding: 12px 8px 12px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.scw-peek__body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.scw-peek__who {
  color: var(--scw-text);
  font-size: 13px;
  font-weight: 600;
}

.scw-peek__title {
  overflow: hidden;
  color: var(--scw-text-4);
  font-size: 11.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scw-peek__text {
  display: -webkit-box;
  margin-top: 2px;
  overflow: hidden;
  color: var(--scw-text-2);
  font-size: 13px;
  line-height: 1.45;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.scw-peek__close {
  flex: none;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin: 8px 8px 0 0;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--scw-text-4);
  font-size: 10px;
  cursor: pointer;
}

.scw-peek__close:hover {
  background: #F4F4F5;
  color: var(--scw-text);
}

.scw-peek-enter-active,
.scw-peek-leave-active {
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.34, 1.3, 0.64, 1);
}

.scw-peek-enter-from,
.scw-peek-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.96);
}

/* ------------------------------- the panel ------------------------------- */
.scw-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 348px;
  height: min(640px, calc(100vh - 140px));
  max-width: calc(100vw - 2 * var(--scw-gap));
  overflow: hidden;
  border: 1px solid var(--scw-border);
  border-radius: 14.5px;
  background: var(--scw-page);
  box-shadow: 0 16px 40px rgba(24, 24, 27, 0.14), 0 2px 8px rgba(24, 24, 27, 0.06);
  outline: none;
  transform-origin: bottom right;
}

/* The app renders at 90% on wide screens (html.app-zoomed): the panel's
   share of the screen is counted before that. */
:global(html.app-zoomed) .scw-panel {
  height: min(640px, calc((100vh - 140px) / 0.9));
}

.scw-panel-enter-active,
.scw-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.24s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.scw-panel-enter-from,
.scw-panel-leave-to {
  opacity: 0;
  transform: translateY(14px) scale(0.97);
}

/* "Chat with us!" — the title, and the minimize and close buttons. */
.scw-head {
  position: relative;
  z-index: 1;
  display: flex;
  flex: none;
  align-items: center;
  height: 60px;
  padding: 0 24px;
  border-bottom: 1px solid var(--scw-border);
  background: var(--scw-surface);
  box-shadow: 0 1px 2px rgba(24, 24, 27, 0.03);
}

.scw-head__title {
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: var(--scw-text);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Back to Home or Messages, in the margin before the title. */
.scw-head__back {
  width: 24px;
  margin: 0 2px 0 -16px;
}

.scw-icon-btn {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--scw-text);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.scw-icon-btn svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.scw-icon-btn:hover {
  background: #F4F4F5;
}

.scw-icon-btn:focus-visible {
  outline: 2px solid var(--scw-accent);
  outline-offset: 1px;
}

/* On Home's tint. */
.scw-icon-btn--hero:hover {
  background: rgba(75, 63, 229, 0.08);
}

/* Each view fills the panel between the header and the navigation. */
.scw-view {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  animation: scw-view-in 0.2s ease-out;
}

@keyframes scw-view-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

/* Loading placeholders: flat, gently pulsing. */
.scw-skeleton {
  display: block;
  border-radius: 6px;
  background: #F0F0F1;
  animation: scw-pulse 1.4s ease-in-out infinite;
}

.scw-skeleton--label {
  width: 30%;
  height: 9px;
  margin-bottom: 12px;
}

.scw-skeleton--line {
  width: 80%;
  height: 10px;
  margin-bottom: 8px;
}

.scw-skeleton--short {
  width: 55%;
  height: 10px;
}

.scw-skeleton--avatar {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
}

@keyframes scw-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* --------------------------------- cards --------------------------------- */
.scw-card {
  display: block;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--scw-border) !important;
  border-radius: 12px;
  background: var(--scw-surface);
  color: var(--scw-text);
  text-align: left;
  box-shadow: 0 4px 14px rgba(24, 24, 27, 0.06);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.scw-card:hover {
  border-color: #D4D4D8 !important;
  box-shadow: 0 6px 18px rgba(24, 24, 27, 0.09);
}

.scw-card:focus-visible {
  outline: 2px solid var(--scw-accent);
  outline-offset: 2px;
}

.scw-card--loading {
  cursor: default;
}

.scw-card--loading:hover {
  border-color: var(--scw-border) !important;
  box-shadow: 0 4px 14px rgba(24, 24, 27, 0.06);
}

.scw-card__label {
  display: block;
  margin-bottom: 10px;
  color: var(--scw-text-3);
  font-size: 12px;
  font-weight: 500;
}

.scw-recent.is-unread .scw-card__label {
  color: var(--scw-accent);
  font-weight: 600;
}

.scw-dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--scw-unread);
}

/* --------------------------------- home ---------------------------------- */
.scw-home {
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* The hello, on a soft tint of the team's colour, with the cards
   overlapping its foot. */
.scw-hero {
  flex: none;
  padding: 16px 16px 66px 22px;
  background: var(--scw-hero);
  color: var(--scw-text);
}

.scw-hero__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 28px;
}

.scw-hero__logo {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  overflow: hidden;
  border-radius: 10px;
  background: #FFFFFF;
  box-shadow: 0 1px 3px rgba(24, 24, 27, 0.08);
}

.scw-hero__logo img {
  width: 64%;
  height: 64%;
  object-fit: contain;
}

.scw-hero__team {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
  overflow: hidden;
  color: var(--scw-text);
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scw-home__title {
  margin: 0;
  color: var(--scw-text);
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.scw-home__wave {
  display: inline-block;
  animation: scw-wave 2.4s ease-in-out 0.4s 2;
  transform-origin: 70% 80%;
}

@keyframes scw-wave {
  0%, 60%, 100% { transform: rotate(0); }
  10%, 30% { transform: rotate(14deg); }
  20%, 40% { transform: rotate(-8deg); }
}

.scw-home__text {
  margin: 4px 0 0;
  color: var(--scw-text-3);
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.015em;
  line-height: 1.25;
}

.scw-home__cards {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: -44px;
  padding: 0 16px 18px;
}

.scw-recent__row {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.scw-recent__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.scw-recent__title,
.scw-recent__preview {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scw-recent__title {
  font-size: 14px;
  font-weight: 500;
}

.scw-recent.is-unread .scw-recent__title {
  font-weight: 600;
}

.scw-recent__preview {
  color: var(--scw-text-3);
  font-size: 13px;
}

.scw-recent.is-unread .scw-recent__preview {
  color: var(--scw-text-2);
}

.scw-recent__meta {
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  color: var(--scw-text-4);
}

.scw-recent__time {
  font-size: 11.5px;
}

.scw-send {
  display: flex;
  align-items: center;
  gap: 12px;
}

.scw-send__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.scw-send__text strong {
  font-size: 14px;
  font-weight: 600;
}

.scw-send__text span {
  color: var(--scw-text-3);
  font-size: 13px;
  line-height: 1.45;
}

.scw-send__icon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--scw-send);
  color: #FFFFFF;
  transition: background-color 0.15s ease;
}

.scw-send__icon svg,
.scw-fab svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.scw-send:hover .scw-send__icon {
  background: var(--scw-accent);
}

.scw-all {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 16px;
}

.scw-all__label {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
}

.scw-all__count {
  min-width: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: #F4F4F5;
  color: var(--scw-text-2);
  font-size: 11.5px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.scw-all .pi {
  color: var(--scw-text-4);
}

/* "Need Help?": a real person, the line to call, and when they answer. */
.scw-help {
  padding: 14px 16px;
  border: 1px solid var(--scw-border);
  border-radius: 12px;
  background: var(--scw-surface);
  color: var(--scw-text);
  box-shadow: 0 4px 14px rgba(24, 24, 27, 0.06);
}

.scw-help__title {
  margin: 0 0 4px;
  color: var(--scw-text);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.scw-help__text {
  margin: 0;
  color: var(--scw-text-3);
  font-size: 13px;
  line-height: 1.5;
}

.scw-help__text + .scw-help__text {
  margin-top: 8px;
}

.scw-help__phone {
  color: var(--scw-accent);
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

.scw-help__phone:hover {
  color: var(--scw-accent-hover);
  text-decoration: underline;
}

.scw-help__phone:focus-visible {
  border-radius: 4px;
  outline: 2px solid var(--scw-accent);
  outline-offset: 2px;
}

.scw-help__hours {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--scw-divider);
}

.scw-help__label {
  margin: 0 0 6px;
  color: var(--scw-text);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
}

.scw-help__list {
  margin: 0;
}

.scw-help__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  line-height: 1.75;
}

.scw-help__row dt {
  color: var(--scw-text-3);
  font-weight: 400;
}

.scw-help__row dd {
  margin: 0;
  color: var(--scw-text);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* ------------------------------- messages -------------------------------- */
.scw-list {
  flex: 1 1 auto;
  min-height: 0;
  padding: 16px 16px 80px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(24, 24, 27, 0.18) transparent;
}

.scw-list::-webkit-scrollbar {
  width: 6px;
}

.scw-list::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(24, 24, 27, 0.18);
}

.scw-list__card {
  overflow: hidden;
  border: 1px solid var(--scw-border);
  border-radius: 10px;
  background: var(--scw-surface);
}

.scw-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.scw-row + .scw-row {
  border-top: 1px solid var(--scw-divider);
}

.scw-row:hover {
  background: #FAFAFA;
}

.scw-row:focus-visible {
  outline: 2px solid var(--scw-accent);
  outline-offset: -2px;
}

.scw-row--skeleton {
  cursor: default;
}

.scw-row--skeleton:hover {
  background: transparent;
}

.scw-row__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.scw-row__top,
.scw-row__bottom {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.scw-row__title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--scw-text);
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scw-row.is-unread .scw-row__title {
  font-weight: 600;
}

.scw-row__time {
  flex: none;
  color: var(--scw-text-4);
  font-size: 11.5px;
}

.scw-row__preview {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--scw-text-3);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scw-row.is-unread .scw-row__preview {
  color: var(--scw-text-2);
}

.scw-row.is-closed .scw-row__title {
  color: var(--scw-text-2);
}

.scw-row__closed {
  flex: none;
  padding: 1px 7px;
  border-radius: 6px;
  background: #F4F4F5;
  color: var(--scw-text-3);
  font-size: 11px;
  font-weight: 500;
}

.scw-list__more {
  display: flex;
  justify-content: center;
  padding: 12px;
  color: var(--scw-text-4);
}

.scw-list__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 52px 28px 24px;
  text-align: center;
}

.scw-list__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 4px;
  border-radius: 50%;
  background: var(--scw-accent-soft);
  color: var(--scw-accent);
  font-size: 20px;
}

.scw-list__empty h3 {
  margin: 0;
  color: var(--scw-text);
  font-size: 15px;
  font-weight: 600;
}

.scw-list__empty p {
  margin: 0;
  color: var(--scw-text-3);
  font-size: 13px;
  line-height: 1.55;
}

/* "Send us a message", floating over the list. */
.scw-fab {
  position: absolute;
  bottom: 16px;
  left: 50%;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  height: 40px;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  background: var(--scw-accent);
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(24, 24, 27, 0.16);
  transform: translateX(-50%);
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.scw-fab:hover {
  background: var(--scw-accent-hover);
  transform: translateX(-50%) translateY(-1px);
}

.scw-fab:focus-visible {
  outline: 2px solid rgba(75, 63, 229, 0.4);
  outline-offset: 2px;
}

/* ----------------------------- a conversation ---------------------------- */
/* "This chat is marked closed by Aron. You can rate the experience now." */
.scw-rate {
  position: relative;
  padding: 32px 16px 16px;
  border: 1px solid var(--scw-border);
  border-radius: 10px;
  background: var(--scw-surface);
  text-align: center;
}

.scw-rate__badge {
  position: absolute;
  top: -22px;
  left: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--scw-accent);
  box-shadow: 0 0 0 4px var(--scw-page);
  transform: translateX(-50%);
}

.scw-rate__badge svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #FFFFFF;
  stroke-width: 1.7;
  stroke-linejoin: round;
}

.scw-rate__text {
  margin: 0 0 10px;
  color: var(--scw-text-2);
  font-size: 14px;
  line-height: 1.6;
}

.scw-rate__stars {
  display: inline-flex;
  gap: 2px;
  margin-bottom: 14px;
}

.scw-rate__star {
  display: inline-flex;
  width: 28px;
  height: 28px;
  padding: 2px;
  border: 0;
  border-radius: 6px;
  background: none;
  color: var(--scw-star-off);
  cursor: pointer;
  transition: color 0.12s ease, transform 0.12s ease;
}

.scw-rate__star.is-on {
  color: var(--scw-star);
}

.scw-rate__star svg {
  width: 100%;
  height: 100%;
  fill: currentColor;
}

.scw-rate__star:hover:not(:disabled) {
  transform: scale(1.12);
}

.scw-rate__star:focus-visible {
  outline: 2px solid var(--scw-accent);
  outline-offset: 1px;
}

.scw-rate__star:disabled {
  cursor: progress;
}

/* Rated: the stars given, nothing more to fill in. */
.scw-rate.is-rated {
  padding-bottom: 18px;
}

.scw-rate__stars--given {
  margin-bottom: 0;
}

.scw-rate__given {
  width: 28px;
  height: 28px;
  padding: 2px;
  fill: var(--scw-star-off);
}

.scw-rate__given.is-on {
  fill: var(--scw-star);
}

/* The apps give every textarea !important borders and sizes: the comment box
   restates its own. */
.scw-rate .scw-rate__comment,
.scw-rate .scw-rate__comment:focus {
  display: block;
  width: 100%;
  height: 76px !important;
  min-height: 76px;
  margin: 0;
  padding: 9px 11px;
  border: 1px solid var(--scw-border) !important;
  border-radius: 8px !important;
  background: var(--scw-surface);
  color: var(--scw-text);
  font-size: 13px !important;
  line-height: 1.5;
  text-align: left;
  resize: none;
  box-shadow: none !important;
  outline: none;
}

.scw-rate .scw-rate__comment:focus {
  border-color: var(--scw-accent) !important;
  box-shadow: 0 0 0 3px rgba(75, 63, 229, 0.12) !important;
}

.scw-rate .scw-rate__comment::placeholder {
  color: var(--scw-text-4);
  font-size: 12.5px;
}

.scw-rate .scw-rate__comment:disabled {
  opacity: 0.6;
}

.scw-rate__error {
  margin: 8px 0 0;
  color: #DC2626;
  font-size: 12.5px;
  text-align: left;
}

.scw-rate__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  margin-top: 10px;
}

.scw-rate__send {
  height: 32px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: var(--scw-accent);
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.scw-rate__send:hover:not(:disabled) {
  background: var(--scw-accent-hover);
}

.scw-rate__send:disabled {
  opacity: 0.6;
  cursor: progress;
}

/* ---------------------------- the navigation ----------------------------- */
.scw-nav {
  display: flex;
  flex: none;
  padding: 6px 0 calc(6px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--scw-divider);
  background: var(--scw-surface);
}

.scw-nav__item {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 6px 0 4px;
  border: 0;
  background: none;
  color: var(--scw-text-4);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;
}

.scw-nav__item:hover {
  color: var(--scw-text-2);
}

.scw-nav__item.is-active {
  color: var(--scw-accent);
  font-weight: 600;
}

.scw-nav__item:focus-visible {
  outline: 2px solid var(--scw-accent);
  outline-offset: -2px;
}

.scw-nav__icon {
  position: relative;
  display: inline-flex;
  font-size: 18px;
}

.scw-nav__badge {
  position: absolute;
  top: -6px;
  right: -11px;
  min-width: 17px;
  height: 17px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--scw-unread);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 700;
  line-height: 17px;
  text-align: center;
  box-shadow: 0 0 0 2px var(--scw-surface);
}

/* --------------------------- phones and tablets -------------------------- */
@media (max-width: 991.98px) {
  .scw--lifted {
    --scw-lift: calc(64px + env(safe-area-inset-bottom, 0px));
  }

  .scw {
    --scw-gap: 16px;
  }
}

/* Phones: the open chat fills the screen, and its own header closes it. */
@media (max-width: 575.98px) {
  .scw-launcher {
    width: 54px;
    height: 54px;
  }

  .scw--open {
    inset: 0;
    gap: 0;
  }

  .scw--open .scw-launcher {
    display: none;
  }

  .scw-panel {
    width: 100%;
    max-width: none;
    height: 100%;
    border: 0;
    border-radius: 0;
  }

  .scw-head {
    height: calc(54px + env(safe-area-inset-top, 0px));
    padding-top: env(safe-area-inset-top, 0px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .scw-launcher,
  .scw-launcher__icon,
  .scw-card,
  .scw-fab,
  .scw-rate__star,
  .scw-panel-enter-active,
  .scw-panel-leave-active,
  .scw-peek-enter-active,
  .scw-peek-leave-active {
    transition: none;
  }

  .scw-home__wave,
  .scw-view,
  .scw-skeleton {
    animation: none;
  }
}
</style>
